import { all, spawn, takeEvery, takeLatest, takeLeading, throttle } from 'redux-saga/effects'

import { poll } from './poll'

/* ------------- Types ------------- */

import { StartupTypes } from '../Redux/StartupRedux'
import { initializationActions, initialize } from 'APP/Store/Initialization'
import { AppSessionTypes } from '../Redux/AppSessionRedux'
import { PatientTypes } from '../Redux/PatientRedux'
import { LoginTypes } from '../Redux/LoginRedux'
import { PatientHistoryTypes } from '../Redux/PatientHistoryRedux'

import { DeeplinkTypes } from '../Redux/DeeplinkRedux'
import { WellnessCenterTypes } from '../Redux/WellnessCenterRedux'
import { HabitTypes } from '../Redux/HabitsRedux'
import { AccountLinkingTypes } from '../Redux/AccountLinkingRedux'
import { featuresActions } from 'APP/Redux/FeaturesSlice'
import { NotificationTypes } from '../Redux/NotificationRedux'
import { ActiveMinutesTypes } from '../Redux/ActiveMinutesRedux'
import { AppointmentBookingTypes } from '../Redux/AppointmentBookingRedux'
import { WellBeingTypes } from '../Redux/WellBeingRedux'

/* ------------- Sagas ------------- */

import {
  startup,
  gotoForeground,
  gotoBackground,
  setIsConnected,
  registerPushToken,
  toggleMandatoryMfa,
  fullStartup,
  registerPushVoipToken,
  handleLaunchStartupSequence,
} from './StartupSagas'

import { watchBrownfieldEvents } from 'APP/Store/Brownfield/sagas'

import {
  login,
  logout,
  requestEmailVerificationResend,
  triggerAppLock,
  activateAppLockToggle,
  checkRenewAuth,
  toggleMfa,
  addRememberedDevice,
  persistLoginData,
  cleanupAuthData,
} from './AuthSagas'

import { updateSprigVisitorAttributes } from './AppSessionSagas'

import {
  getOrCreateIcbtEpisode,
  submitPost,
  retryPost,
  updateChannelLastViewed,
  receiveEvent,
  mattermostStart,
  fetchChannels,
  fetchAllPostsForChannel,
  fetchNewPostsForChannel,
  fetchOlderPostsForChannel,
  launchEpisodeCommand,
  getOrCreateWbiEpisode,
  navigateToLink,
  fetchAppointments,
  rescheduleAppointment,
  cancelAppointment,
  resetTypingEventDataAfterDelay,
  fetchEpisode,
  fetchEpisodeMembers,
  fetchPractitionerBio,
  archiveEpisode,
  cancelAppointmentFromCalendar,
  goBackInEpisode,
  uploadFile,
  removeUploadedFile,
} from './PatientHistorySagas'

import {
  fetchPatientProfile,
  fetchScribeV2User,
  navigateWithLocationPermission,
  navigateWithPermission,
  navigateWithMandatoryMfa,
  patientProfileGiveConsent,
  requestLocationPermission,
  redirectToLocationService,
  updateBasicProfile,
  updateProfile,
  updateIdCard,
  registerCreditCard,
  chargeCreditCard,
  changeEmail,
  enrolWithEnrolmentCodes,
  unchallengedInvitationSearch,
  challengedInvitationQueueNext,
  resendInvitation,
  challengeInvitation,
  navigateToGeolocationCheck,
  requestPostNotifications,
  authorizeAndChangeEmail,
  runStartupAfterChangeEmail,
  navigateToCommitmentScreen,
  navigateWithEmailVerified,
  getPillwayAccessToken,
} from './PatientSagas'

import { processDeferredDeeplink } from './DeeplinkSaga'

import {
  fetchContent,
  getMyChallenges,
  getNewChallenges,
  getCompletedChallenges,
  getChallenge,
  joinIndividualChallenge,
  joinTeamChallenge,
  getLeaderboard,
  getTeamParticipants,
  leaveSelectedChallenge,
  connectTracker,
  disconnectTracker,
  addMetricsTracker,
} from './WellnessCenterSagas'

import {
  getCategories,
  getSingleHabit,
  getHabitList,
  getHabitContent,
  getTopics,
  startHabit,
  removeHabit,
  doneHabit,
  undoneHabit,
  getDailyProgressList,
  getSingleHabitStats,
  getSingleHabitProgress,
  removeHabitPrompt,
  updateHabitPrompt,
  onHabitNotificationFeatureChange,
  cancelAllHabitLocalNotifications,
  getAllNotificationConfigurations,
  getNotificationConfigurations,
  updateNotificationConfiguration,
  watchForHabitNotificationSafetyCheck,
  ensureNotificationParity,
} from './HabitsSagas'
import { requestAccountLinking } from './AccountLinkingSagas'

import {
  updateNotificationPreference,
  triggerNotificationPermissionModal,
} from './NotificationSagas'

import {
  getActiveMinutes,
  getConnectedTrackers,
  getHistory,
  setConfettiShown,
  updateWeeklyTarget,
  requestNewConnection,
  removeConnection,
  getChallenges,
  getMyCompletedChallenges,
  getSingleChallenge,
  joinChallenge,
  getChallengeLeaderboard,
  leaveChallenge,
  handleAppleHealthReconnection,
} from './ActiveMinutesSagas'
import { isAndroid, isIOS, isMobile } from 'APP/Helpers/checkPlatform'
import { isEmbeddedEnvironment } from 'APP/BrownfieldBridge/utils'
import { getLatestWellBeingAssessment, getRecommendations } from './WellBeingSagas'
import { initTerraConnection } from './TerraSagas'
import {
  bookAppointment,
  watchGetAvailableAppointments,
  getAvailabilitiesExists,
} from './AppointmentBookingSagas'

import { familyActions, familySagas } from 'APP/Store/Family'
import { contentActions, contentSagas } from 'APP/Store/Content'
import { providerBookingActions, providerBookingSagas } from 'APP/Store/ProviderBooking'
import { videoSessionActions, videoSessionSagas } from 'APP/Store/VideoSession'
import { pharmaciesActions, pharmaciesSagas } from 'APP/Store/Pharmacies'
import { nativeCallIOSActions, nativeCallIOSSagas } from 'APP/Store/NativeCallIOS'
import { nativeCallAndroidActions, nativeCallAndroidSagas } from 'APP/Store/NativeCallAndroid'
import { placesActions, placesSagas } from 'APP/Store/Places'
import { VIDEO_CALL_END_ACTIONS } from 'APP/Store/VideoSession/slice'
import { semanticSearchActions, semanticSearchSagas } from 'APP/Store/SemanticSearch'
import { eligibilitySearchActions, eligibilitySearchSagas } from 'APP/Store/EligibilitySearch'
import { memberTasks, memberTasksSagas } from 'APP/Store/MemberTasks'
import { preloadActions } from 'APP/Store/Preload'
import { preload } from 'APP/Store/Preload/sagas'

/* ------------- Connect Types To Sagas ------------- */

export default function* root() {
  yield all([
    isEmbeddedEnvironment() && spawn(watchBrownfieldEvents),
    isEmbeddedEnvironment() && takeLatest(preloadActions.preload, preload),
    // Video Session and OpenTok related
    takeLeading(videoSessionActions.patientAcceptedCall, videoSessionSagas.patientAcceptedCall),
    takeLeading(videoSessionActions.patientDeclinedCall, videoSessionSagas.patientDeclinedCall),
    takeLatest(videoSessionActions.onDemandCallRequested, videoSessionSagas.onDemandCallRequested),
    takeLatest(videoSessionActions.sessionDisconnected, videoSessionSagas.sessionDisconnected),
    throttle(5000, videoSessionSagas.Types.CONNECT_TO_OPENTOK, videoSessionSagas.connectToOpenTok),
    takeLatest(VIDEO_CALL_END_ACTIONS, videoSessionSagas.dismissVideoCallScreen),
    // TODO (DIA-87439): Move to dismissVideoCallScreen once it no longer runs multiple times
    // per video call session
    takeLatest(
      [videoSessionActions.patientEndedCall, videoSessionActions.platformEndedCall],
      videoSessionSagas.afterCallAppRatingPromptCheck
    ),
    takeEvery(videoSessionActions.trackVideoQualityStats, videoSessionSagas.trackVideoQualityStats),
    // NativeCall is platform specific.
    ...(isIOS()
      ? [
          spawn(nativeCallIOSSagas.attachNativeCallListeners),
          takeLatest(nativeCallIOSActions.callAccepted, nativeCallIOSSagas.callAccepted),
          takeLatest(nativeCallIOSActions.endNativeCall, nativeCallIOSSagas.endNativeCall),
          takeLatest(
            nativeCallIOSActions.nativeCallAccepted,
            nativeCallIOSSagas.nativeCallAccepted
          ),
          takeLatest(
            nativeCallIOSActions.nativeCallDeclined,
            nativeCallIOSSagas.nativeCallDeclined
          ),
        ]
      : []),
    ...(isAndroid()
      ? [
          takeLatest(
            nativeCallAndroidActions.dismissVideoCall,
            nativeCallAndroidSagas.dismissVideoCall
          ),
          takeLatest(
            nativeCallAndroidActions.incomingFullScreenCall,
            nativeCallAndroidSagas.incomingFullScreenCall
          ),
          takeLatest(nativeCallAndroidActions.callAccepted, nativeCallAndroidSagas.callAccepted),
          takeLatest(
            nativeCallAndroidActions.notificationCallAccepted,
            nativeCallAndroidSagas.notificationCallAccepted
          ),
          takeLatest(nativeCallAndroidActions.callEnded, nativeCallAndroidSagas.callEnded),
          takeLatest(
            nativeCallAndroidActions.notificationCallEnded,
            nativeCallAndroidSagas.notificationCallEnded
          ),
          takeLatest(
            nativeCallAndroidActions.showOngoingCallNotification,
            nativeCallAndroidSagas.showOngoingCallNotification
          ),
        ]
      : []),

    takeLatest(placesActions.getPlaces, placesSagas.getPlaces),

    takeLatest(initializationActions.initialize, initialize),
    takeEvery(StartupTypes.STARTUP, startup),
    takeLatest([StartupTypes.FULL_STARTUP, LoginTypes.LOGOUT], fullStartup),
    takeLatest(StartupTypes.LAUNCH_STARTUP_SEQUENCE, handleLaunchStartupSequence),
    // @ts-expect-error not migrated to RTK yet
    takeLatest(StartupTypes.SET_PUSH_TOKEN_REQUEST, registerPushToken),
    // @ts-expect-error not migrated to RTK yet
    takeLatest(StartupTypes.SET_PUSH_VOIP_TOKEN_REQUEST, registerPushVoipToken),
    takeLatest(StartupTypes.SET_NOTIFICATION_PERMISSION_ACCEPTED, navigateWithPermission),
    takeLatest(StartupTypes.SET_LOCATION_PERMISSION_ACCEPTED, navigateWithLocationPermission),
    takeLatest(StartupTypes.TRIGGER_APP_LOCK, triggerAppLock),
    takeLatest(StartupTypes.ACTIVATE_APP_LOCK_TOGGLE, activateAppLockToggle),
    takeLatest(StartupTypes.MANDATORY_MFA_TOGGLE_REQUEST, toggleMandatoryMfa),
    takeLatest(StartupTypes.MANDATORY_MFA_TOGGLED, navigateWithMandatoryMfa),

    // @ts-expect-error not migrated to RTK yet
    takeLatest(AppSessionTypes.SET_IS_CONNECTED, setIsConnected),
    takeLatest(AppSessionTypes.SET_APP_FOREGROUNDED, gotoForeground),
    takeLatest(AppSessionTypes.SET_APP_BACKGROUNDED, gotoBackground),

    takeLatest(DeeplinkTypes.PROCESS_DEFERRED_DEEPLINK, processDeferredDeeplink),

    takeLatest(LoginTypes.LOGIN_REQUEST, login, {
      triggerStartupNav: true,
      triggerLogoutOnFailure: false,
    }),

    takeLatest(LoginTypes.ACCOUNT_LINK_LOGIN_REQUEST, login, {
      triggerStartupNav: true,
      triggerLogoutOnFailure: false,
      saveSecondaryAccessToken: true,
    }),

    takeLatest(LoginTypes.LOGIN_REQUEST_WITH_CONNECTION, login, {
      triggerStartupNav: true,
      triggerLogoutOnFailure: true,
      isEphemeralSession: false,
    }),

    takeLatest(LoginTypes.SILENT_LOGIN_REQUEST_WITH_CONNECTION, login, {
      triggerStartupNav: true,
      triggerLogoutOnFailure: true,
      triggerSilentlogin: true,
      isEphemeralSession: false,
    }),

    takeLatest(LoginTypes.LOGIN_REQUEST_WITH_EXCHANGE_TOKEN, login, {
      triggerStartupNav: true,
      triggerLogoutOnFailure: true,
    }),

    takeLatest(LoginTypes.SIGNUP_REQUEST, login, {
      triggerStartupNav: true,
      triggerLogoutOnFailure: false,
      openSignUp: true,
    }),

    takeLatest(LoginTypes.SIGNUP_REQUEST_WITH_CONNECTION, login, {
      triggerStartupNav: true,
      triggerLogoutOnFailure: false,
      openSignUp: true,
    }),

    takeLatest(LoginTypes.LOGOUT, logout),
    takeLatest(LoginTypes.LOGOUT, cleanupAuthData),
    takeLatest(LoginTypes.LOGOUT, videoSessionSagas.disconnect),
    takeLatest(LoginTypes.LOGOUT, memberTasksSagas.resetMemberTasks),
    takeLatest(LoginTypes.EMAIL_VERIFICATION_RESEND_REQUEST, requestEmailVerificationResend),
    takeLatest(LoginTypes.TOGGLE_MFA_REQUEST, toggleMfa),
    takeLatest(LoginTypes.SUBMIT_PUBLIC_COMPUTER, addRememberedDevice),

    takeLatest([LoginTypes.LOGIN_SUCCESS, LoginTypes.REFRESH_AUTH_SUCCESS], persistLoginData),
    poll(
      LoginTypes.START_REFRESH_AUTH_POLL,
      LoginTypes.STOP_REFRESH_AUTH_POLL,
      5 * 60000, // we check to refresh auth every 5 minutes
      checkRenewAuth
    ),
    // @ts-expect-error not migrated to RTK yet
    takeEvery(PatientHistoryTypes.RETRY_POST_REQUEST, retryPost),
    // @ts-expect-error not migrated to RTK yet
    takeLatest(PatientHistoryTypes.FETCH_ALL_POSTS_FOR_CHANNEL, fetchAllPostsForChannel),
    // @ts-expect-error not migrated to RTK yet
    takeEvery(PatientHistoryTypes.FETCH_NEW_POSTS_FOR_CHANNEL, fetchNewPostsForChannel),
    // @ts-expect-error not migrated to RTK yet
    takeEvery(PatientHistoryTypes.FETCH_OLDER_POSTS_FOR_CHANNEL, fetchOlderPostsForChannel),
    // @ts-expect-error not migrated to RTK yet
    takeEvery(PatientHistoryTypes.SUBMIT_TEXT_POST_REQUEST, submitPost),
    // @ts-expect-error not migrated to RTK yet
    takeEvery(PatientHistoryTypes.SUBMIT_FILE_POST_REQUEST, submitPost),
    // @ts-expect-error not migrated to RTK yet
    takeEvery(PatientHistoryTypes.SUBMIT_ANSWER_POST_REQUEST, submitPost),
    // @ts-expect-error not migrated to RTK yet
    takeEvery(PatientHistoryTypes.SUBMIT_TEXT_WITH_PAYLOAD_POST_REQUEST, submitPost),
    // @ts-expect-error not migrated to RTK yet
    takeLatest(PatientHistoryTypes.UPLOAD_FILE_REQUEST, uploadFile),
    // @ts-expect-error not migrated to RTK yet
    takeLatest(PatientHistoryTypes.REMOVE_UPLOADED_FILE_REQUEST, removeUploadedFile),
    // @ts-expect-error not migrated to RTK yet
    takeEvery(PatientHistoryTypes.UPDATE_CHANNEL_LAST_VIEWED, updateChannelLastViewed),
    // @ts-expect-error not migrated to RTK yet
    takeLatest(PatientHistoryTypes.LAUNCH_EPISODE_COMMAND, launchEpisodeCommand),
    takeLatest(PatientHistoryTypes.GET_OR_CREATE_ICBT_EPISODE_REQUEST, getOrCreateIcbtEpisode),
    takeLatest(PatientHistoryTypes.RECEIVE_EVENT, receiveEvent),

    takeLatest(PatientHistoryTypes.MATTERMOST_START, mattermostStart),
    takeLatest(PatientHistoryTypes.FETCH_CHANNELS_REQUEST, fetchChannels),
    // @ts-expect-error not migrated to RTK yet
    takeLatest(PatientHistoryTypes.RESCHEDULE_APPOINTMENT_REQUEST, rescheduleAppointment),
    // @ts-expect-error not migrated to RTK yet
    takeLatest(PatientHistoryTypes.CANCEL_APPOINTMENT_REQUEST, cancelAppointment),
    takeLatest(
      // @ts-expect-error not migrated to RTK yet
      PatientHistoryTypes.CANCEL_APPOINTMENT_FROM_CALENDAR_REQUEST,
      cancelAppointmentFromCalendar
    ),
    takeLatest(PatientHistoryTypes.REFRESH_APPOINTMENTS_REQUEST, fetchAppointments),
    // @ts-expect-error not migrated to RTK yet
    takeLatest(PatientHistoryTypes.FETCH_PRACTITIONER_BIO, fetchPractitionerBio),

    takeLatest(PatientHistoryTypes.RESET_TYPING_EVENT_DATA, resetTypingEventDataAfterDelay),
    // @ts-expect-error not migrated to RTK yet
    takeLatest(PatientHistoryTypes.FETCH_ONE_EPISODE_REQUEST, fetchEpisode),
    // @ts-expect-error not migrated to RTK yet
    takeLatest(PatientHistoryTypes.FETCH_EPISODE_MEMBERS_REQUEST, fetchEpisodeMembers),
    // @ts-expect-error not migrated to RTK yet
    takeLeading(PatientHistoryTypes.ARCHIVE_EPISODE_REQUEST, archiveEpisode),
    // @ts-expect-error not migrated to RTK yet
    takeLeading(PatientHistoryTypes.GO_BACK_IN_EPISODE_REQUEST, goBackInEpisode),

    takeLatest(PatientTypes.PATIENT_CREDIT_CARD_REGISTER_REQUEST, registerCreditCard),
    takeLatest(PatientTypes.PATIENT_CREDIT_CARD_CHARGE, chargeCreditCard),
    takeLatest(PatientTypes.PATIENT_PROFILE_FETCH_REQUEST, fetchPatientProfile),
    takeLatest(PatientTypes.SCRIBE_V2_USER_FETCH_TRIGGER, fetchScribeV2User),
    // @ts-expect-error not migrated to RTK yet
    takeLatest(PatientTypes.PATIENT_PROFILE_GIVE_CONSENT, patientProfileGiveConsent),
    takeLatest(PatientTypes.PATIENT_PROFILE_BASIC_UPDATE_REQUEST, updateBasicProfile),
    takeLatest(PatientTypes.SCRIBE_V2_USER_FETCH_SUCCESS, updateSprigVisitorAttributes),
    takeLatest(PatientTypes.PILLWAY_AUTHENTICATION_REQUEST, getPillwayAccessToken),
    // @ts-expect-error not migrated to RTK yet
    takeLatest(PatientTypes.PATIENT_LOCATION_PERMISSION_REQUEST, requestLocationPermission),
    // @ts-expect-error not migrated to RTK yet
    takeLatest(PatientTypes.PATIENT_LOCATION_SERVICE_ACCESS, redirectToLocationService),
    takeLatest(PatientTypes.PATIENT_PROFILE_UPDATE_REQUEST, updateProfile),
    takeLatest(PatientTypes.PATIENT_ID_CARD_UPDATE_REQUEST, updateIdCard),
    takeLatest(PatientTypes.PATIENT_ENROL_WITH_ENROLMENT_CODES_REQUEST, enrolWithEnrolmentCodes),
    takeLatest(PatientTypes.RESEND_INVITATION_REQUEST, resendInvitation),
    takeLeading(PatientTypes.PATIENT_EMAIL_CHANGE_REQUEST, changeEmail),
    takeLeading(PatientTypes.UNCHALLENGED_INVITATION_SEARCH_REQUEST, unchallengedInvitationSearch),
    takeLatest(PatientTypes.CHALLENGED_INVITATION_QUEUE_REQUEST, challengedInvitationQueueNext),
    takeLatest(PatientTypes.CHALLENGED_INVITATION_QUEUE_NEXT, challengedInvitationQueueNext),
    // @ts-expect-error not migrated to RTK yet
    takeLatest(PatientTypes.CHALLENGE_INVITATION_REQUEST, challengeInvitation),
    takeLatest(PatientTypes.SUBMIT_COMMITMENT, navigateToGeolocationCheck),
    takeLatest(PatientTypes.ONBOARDING_DEFERRED_ENROLMENT_COMPLETE, navigateToCommitmentScreen),
    takeLatest(PatientTypes.POST_NOTIFICATIONS_REQUEST, requestPostNotifications),
    // @ts-expect-error not migrated to RTK yet
    takeLatest(PatientTypes.CHANGE_EMAIL_REQUEST, authorizeAndChangeEmail),
    takeLatest(PatientTypes.RUN_STARTUP_AFTER_CHANGE_EMAIL, runStartupAfterChangeEmail),

    takeLatest(familyActions.fetchFamilyRequest, familySagas.fetchFamily),
    takeLatest(familyActions.adultInviteRequest, familySagas.addAdult),
    takeLatest(familyActions.cancelAdultInviteRequest, familySagas.removeAdult),
    takeLatest(familyActions.acceptFamilyInviteRequest, familySagas.acceptFamilyInvite),
    takeLatest(familyActions.createChildProfileRequest, familySagas.createChildProfile),
    takeLatest(familyActions.updateChildProfileRequest, familySagas.updateChildProfile),
    takeLatest(
      familyActions.recordChildConsentRequest,
      familySagas.recordConsentAndCreateChildProfile
    ),

    // @ts-expect-error not migrated to RTK yet
    takeLatest(WellnessCenterTypes.SET_CONTENT, fetchContent),
    takeLatest(WellnessCenterTypes.GET_MY_CHALLENGES, getMyChallenges),
    takeLatest(WellnessCenterTypes.GET_NEW_CHALLENGES, getNewChallenges),
    takeLatest(WellnessCenterTypes.GET_COMPLETED_CHALLENGES, getCompletedChallenges),
    // @ts-expect-error not migrated to RTK yet
    takeLatest(WellnessCenterTypes.GET_CHALLENGE, getChallenge),
    // @ts-expect-error not migrated to RTK yet
    takeLatest(WellnessCenterTypes.JOIN_INDIVIDUAL_CHALLENGE, joinIndividualChallenge),
    takeLatest(WellnessCenterTypes.LEAVE_SELECTED_CHALLENGE, leaveSelectedChallenge),
    // @ts-expect-error not migrated to RTK yet
    takeLatest(WellnessCenterTypes.JOIN_TEAM_CHALLENGE, joinTeamChallenge),
    // @ts-expect-error not migrated to RTK yet
    takeLatest(WellnessCenterTypes.GET_LEADERBOARD, getLeaderboard),
    // @ts-expect-error not migrated to RTK yet
    takeLatest(WellnessCenterTypes.GET_TEAM_PARTICIPANTS, getTeamParticipants),
    // @ts-expect-error not migrated to RTK yet
    takeLatest(WellnessCenterTypes.CONNECT_TRACKER, connectTracker),
    // @ts-expect-error not migrated to RTK yet
    takeLatest(WellnessCenterTypes.DISCONNECT_TRACKER, disconnectTracker),
    // @ts-expect-error not migrated to RTK yet
    takeEvery(WellnessCenterTypes.ADD_METRICS_TRACKER, addMetricsTracker),
    // @ts-expect-error not migrated to RTK yet
    takeLatest(WellnessCenterTypes.CONNECT_TRACKER, initTerraConnection),
    // @ts-expect-error not migrated to RTK yet
    takeLeading(ActiveMinutesTypes.REQUEST_NEW_CONNECTION, requestNewConnection),
    // @ts-expect-error not migrated to RTK yet
    takeLeading(ActiveMinutesTypes.REMOVE_CONNECTION, removeConnection),
    takeLatest(ActiveMinutesTypes.GET_ACTIVE_MINUTES, getActiveMinutes),
    // @ts-expect-error not migrated to RTK yet
    takeLatest(ActiveMinutesTypes.GET_HISTORY, getHistory),
    // @ts-expect-error not migrated to RTK yet
    takeLatest(ActiveMinutesTypes.UPDATE_WEEKLY_TARGET, updateWeeklyTarget),
    takeLatest(ActiveMinutesTypes.SET_CONFETTI_SHOWN, setConfettiShown),
    takeLatest(ActiveMinutesTypes.GET_CONNECTED_TRACKERS, getConnectedTrackers),
    takeLatest(ActiveMinutesTypes.GET_CHALLENGES, getChallenges),
    takeLatest(ActiveMinutesTypes.GET_MY_COMPLETED_CHALLENGES, getMyCompletedChallenges),
    // @ts-expect-error not migrated to RTK yet
    takeLatest(ActiveMinutesTypes.GET_SINGLE_CHALLENGE, getSingleChallenge),
    // @ts-expect-error not migrated to RTK yet
    takeLatest(ActiveMinutesTypes.JOIN_CHALLENGE, joinChallenge),
    // @ts-expect-error not migrated to RTK yet
    takeLatest(ActiveMinutesTypes.GET_CHALLENGE_LEADERBOARD, getChallengeLeaderboard),
    // @ts-expect-error not migrated to RTK yet
    takeLatest(ActiveMinutesTypes.LEAVE_CHALLENGE, leaveChallenge),
    takeLatest(ActiveMinutesTypes.HANDLE_APPLE_HEALTH_RECONNECTION, handleAppleHealthReconnection),

    takeLatest(HabitTypes.GET_CATEGORIES, getCategories),
    // @ts-expect-error not migrated to RTK yet
    takeLatest(HabitTypes.GET_TOPICS, getTopics),
    // @ts-expect-error not migrated to RTK yet
    takeLatest(HabitTypes.GET_SINGLE_HABIT, getSingleHabit),
    // @ts-expect-error not migrated to RTK yet
    takeLatest(HabitTypes.GET_SINGLE_HABIT_STATS, getSingleHabitStats),
    // @ts-expect-error not migrated to RTK yet
    takeLatest(HabitTypes.GET_SINGLE_HABIT_PROGRESS, getSingleHabitProgress),
    // @ts-expect-error not migrated to RTK yet
    takeLatest(HabitTypes.GET_HABIT_LIST, getHabitList),
    // @ts-expect-error not migrated to RTK yet
    takeLatest(HabitTypes.GET_HABIT_CONTENT, getHabitContent),
    // @ts-expect-error not migrated to RTK yet
    takeLatest(HabitTypes.START_HABIT, startHabit),
    // @ts-expect-error not migrated to RTK yet
    takeLatest(HabitTypes.REMOVE_HABIT, removeHabit),
    // @ts-expect-error not migrated to RTK yet
    takeLeading(HabitTypes.DONE_HABIT, doneHabit),
    // @ts-expect-error not migrated to RTK yet
    takeLeading(HabitTypes.UNDONE_HABIT, undoneHabit),
    // @ts-expect-error not migrated to RTK yet
    takeLatest(HabitTypes.GET_DAILY_PROGRESS_LIST, getDailyProgressList),
    // @ts-expect-error not migrated to RTK yet
    takeLatest(HabitTypes.UPDATE_HABIT_PROMPT, updateHabitPrompt),
    // @ts-expect-error not migrated to RTK yet
    takeLatest(HabitTypes.REMOVE_HABIT_PROMPT, removeHabitPrompt),
    takeLatest(HabitTypes.CANCEL_ALL_HABIT_LOCAL_NOTIFICATIONS, cancelAllHabitLocalNotifications),
    takeLatest(HabitTypes.GET_ALL_NOTIFICATION_CONFIGURATIONS, getAllNotificationConfigurations),
    // @ts-expect-error not migrated to RTK yet
    takeLatest(HabitTypes.GET_NOTIFICATION_CONFIGURATIONS, getNotificationConfigurations),
    // @ts-expect-error not migrated to RTK yet
    takeLatest(HabitTypes.UPDATE_NOTIFICATION_CONFIGURATION, updateNotificationConfiguration),
    takeLatest(HabitTypes.ENSURE_NOTIFICATION_PARITY, ensureNotificationParity),

    takeLatest(
      [
        featuresActions.setFeatures,
        featuresActions.setOverrides,
        featuresActions.resetFeatures,
        PatientTypes.SCRIBE_V2_USER_FETCH_SUCCESS,
        AppSessionTypes.SET_APP_FOREGROUNDED,
      ],
      onHabitNotificationFeatureChange
    ),
    isMobile() && watchForHabitNotificationSafetyCheck(ensureNotificationParity),
    takeLatest(AccountLinkingTypes.REQUEST_ACCOUNT_LINKING, requestAccountLinking),

    takeLatest(contentActions.getCmsFeed, contentSagas.fetchCmsFeedContent),
    takeLeading(contentActions.getServiceGroups, contentSagas.fetchServiceGroups),
    takeEvery(contentActions.getOneServiceGroup, contentSagas.fetchOneServiceGroup),
    takeEvery(contentActions.getExternalService, contentSagas.fetchExternalService),

    takeLatest(NotificationTypes.UPDATE_NOTIFICATION_PREFERENCE, updateNotificationPreference),
    takeLatest(
      NotificationTypes.TRIGGER_NOTIFICATION_PERMISSION_MODAL,
      triggerNotificationPermissionModal
    ),

    takeLatest(PatientHistoryTypes.GET_OR_CREATE_WBI_EPISODE_REQUEST, getOrCreateWbiEpisode),
    // @ts-expect-error not migrated to RTK yet
    takeLatest(PatientHistoryTypes.NAVIGATE_TO_LINK, navigateToLink),

    takeLatest(WellBeingTypes.GET_LATEST, getLatestWellBeingAssessment),
    takeLatest(WellBeingTypes.GET_RECOMMENDATIONS, getRecommendations),

    takeLatest(PatientTypes.SKIP_EMAIL_VERIFICATION, navigateWithEmailVerified),

    spawn(watchGetAvailableAppointments),
    // @ts-expect-error not migrated to RTK yet
    takeLatest(AppointmentBookingTypes.GET_AVAILABILITIES_EXISTS, getAvailabilitiesExists),
    // @ts-expect-error not migrated to RTK yet
    takeLatest(AppointmentBookingTypes.CREATE_APPOINTMENT, bookAppointment),

    spawn(providerBookingSagas.watchGetAvailabilities),
    spawn(providerBookingSagas.watchGetProviderAvailabilities),
    takeLatest(providerBookingActions.getBooking, providerBookingSagas.getBooking),
    takeLatest(providerBookingActions.updateBooking, providerBookingSagas.updateBooking),
    takeLatest(providerBookingActions.createAppointment, providerBookingSagas.bookAppointment),
    takeLatest(providerBookingActions.getProviders, providerBookingSagas.getProviders),
    takeLatest(providerBookingActions.getProviderBio, providerBookingSagas.getProviderBio),
    takeLatest(providerBookingActions.getProviderById, providerBookingSagas.getProviderById),
    takeLatest(
      providerBookingActions.getProvidersAttributes,
      providerBookingSagas.getProvidersAttributes
    ),
    takeLatest(
      providerBookingActions.getAppointmentTypes,
      providerBookingSagas.getAppointmentTypes
    ),
    takeLatest(pharmaciesActions.getPharmacies, pharmaciesSagas.getPharmacies),
    takeLatest(semanticSearchActions.runSemanticSearch, semanticSearchSagas.runSemanticSearch),
    takeLatest(
      eligibilitySearchActions.checkEligibilityAccess,
      eligibilitySearchSagas.checkEligibilityAccess
    ),
    takeLatest(
      eligibilitySearchActions.searchOrganizations,
      eligibilitySearchSagas.searchOrganizations
    ),
    takeLatest(
      eligibilitySearchActions.enrolWithOrganizationId,
      eligibilitySearchSagas.enrolWithOrganizationId
    ),
    takeLatest(memberTasks.fetchTasks, memberTasksSagas.fetchTasks),
  ])
}
